import { Flex, Form, Select } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { DefectReportHistoryFilterData } from 'business/quality-control/pages/defect-report-history';
import { useFilterOptions } from 'business/quality-control/pages/defect-report-history/hooks/use-filter-options';
import {
  DefectRange,
  defaultFilters,
  useDefectReportHistoryFilters,
  useDefectReportHistoryFiltersFormName,
  useResetDefectReportHistoryFilters,
} from 'business/quality-control/pages/defect-report-history/providers/filters';
import { useGetDefectIndicatorData } from 'business/quality-control/pages/record-segment-defect/hooks/data/get-defect-indicator';
import { useGetDefectReportTypeData } from 'business/quality-control/pages/record-segment-defect/hooks/data/get-defect-report-type';
import { FilterData } from 'technical/filter-data';
import { useMediaType } from 'technical/media/hooks';
import Button from 'ui/button';
import { SelectedItemsFooter } from 'ui/filter-footer';
import { DateRangePicker } from 'ui/form/date-range-picker';
import { NumberRangeInputWithMessage } from 'ui/form/number-range-input-with-message';

import styles from './index.module.scss';

export const DefectReportFilters = ({
  filterData,
}: {
  filterData?: FilterData<DefectReportHistoryFilterData>;
}) => {
  const { isMobile } = useMediaType();
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const { t } = useTranslation();
  const [form] = Form.useForm();

  const formName = useDefectReportHistoryFiltersFormName();
  const filters = useDefectReportHistoryFilters();
  const resetFilters = useResetDefectReportHistoryFilters();

  const { data: defectReportData, isLoading: isDefectReportLoading } =
    useGetDefectReportTypeData(currentConstructionSite.id);

  const { indicators, hasIndicators } = useGetDefectIndicatorData(
    currentConstructionSite.id,
  );

  const {
    defectTypeOptions,
    indicatorOptions,
    defectReportDateOptions,
    statusOptions,
    defectRangeOptions,
  } = useFilterOptions(defectReportData, indicators);

  return (
    <>
      <Form layout="inline" form={form} name={formName} initialValues={filters}>
        <Form.Item
          name="defectType"
          label={t('input.defectReportTypes.selectLabel')}
          layout="vertical"
        >
          <Select
            placeholder={t('input.defectReportTypes.placeholder')}
            options={defectTypeOptions ?? []}
            allowClear
            loading={isDefectReportLoading}
            className={styles.defectTypeSelect}
          />
        </Form.Item>

        <Form.Item
          name="toRepair"
          label={t('input.toRepair.selectLabel')}
          layout="vertical"
        >
          <Select
            placeholder={t('input.toRepair.placeholder')}
            allowClear
            options={[
              { label: t('common.booleanValue.state_true'), value: true },
              { label: t('common.booleanValue.state_false'), value: false },
            ]}
            className={styles.toRepairSelect}
          />
        </Form.Item>

        {hasIndicators ? (
          <Form.Item
            name="indicator"
            label={t('input.indicator.selectLabel')}
            layout="vertical"
          >
            <Select
              placeholder={t('input.indicator.placeholder')}
              options={indicatorOptions}
              allowClear
              className={styles.indicatorSelect}
            />
          </Form.Item>
        ) : null}

        <Flex vertical={isMobile}>
          <Form.Item
            name="defectReportHistoryDate"
            label={t('input.defectReportDate.label')}
            layout="vertical"
          >
            <Select
              placeholder={t('input.defectReportDate.placeholder')}
              allowClear
              options={defectReportDateOptions}
            />
          </Form.Item>
          <Form.Item
            name="defectReportHistoryDateRange"
            label={isMobile ? null : ' '}
            layout="vertical"
          >
            <DateRangePicker allowClear format="L" />
          </Form.Item>
        </Flex>

        <Form.Item
          name="status"
          label={t('input.status.label')}
          layout="vertical"
        >
          <Select
            placeholder={t('input.status.placeholder')}
            mode="multiple"
            allowClear
            options={statusOptions}
            className={styles.statusSelect}
          />
        </Form.Item>

        <Form.Item
          name="defectRange"
          label={t('input.defectRange.selectLabel')}
          layout="vertical"
        >
          <Select
            placeholder={t('input.defectRange.placeholder')}
            options={defectRangeOptions}
            allowClear
          />
        </Form.Item>
        {filters.defectRange === DefectRange.interval ? (
          <NumberRangeInputWithMessage
            name="ringNumberRange"
            label={t('input.segmentRange.rangeLabel')}
            leftPlaceholder={t('input.ringRange.startPlaceholder')}
            rightPlaceholder={t('input.ringRange.endPlaceholder')}
          />
        ) : null}
      </Form>
      {isMobile ? (
        <Flex justify="center">
          <Button
            onClick={() => {
              form.setFieldsValue(defaultFilters);
              resetFilters();
            }}
          >
            {t('common.clearFilter')}
          </Button>
        </Flex>
      ) : null}
      {filterData?.selectedDefectReports ? (
        <SelectedItemsFooter mobile>
          {t('qualityControl.defectReportHistory.selectedItems', {
            selectedDefectReports: filterData?.selectedDefectReports,
          })}
        </SelectedItemsFooter>
      ) : null}
    </>
  );
};
